import { AfterViewChecked, AfterViewInit, Component, ComponentFactoryResolver, ComponentRef, EventEmitter, Input, Output, QueryList, ViewChildren, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CompactType, DisplayGrid, GridsterConfig, GridType } from 'angular-gridster2';
import cloneDeep from 'lodash/cloneDeep';
import { UserBoard, UserBoardComponentMode, UserBoardItem, UserBoardTypes } from 'src/app/services/models/user-board.model';
import { FilterBar } from 'src/app/services/models/filter-bar.model';
import { FilterBarService } from 'src/app/services/filter-bar.service';
import { BoardItemCreatorComponent, BoardItemCreatorModalObject } from '../../board-item-creator/board-item-creator.component';
import { HeroTileComponent } from '../../board-tiles/hero-tile/hero-tile.component';
import { ChartTileComponent } from '../../board-tiles/chart-tile/chart-tile.component';
import { TableTileComponent } from '../../board-tiles/table-tile/table-tile.component';
import { PieTileComponent } from '../../board-tiles/pie-tile/pie-tile.component';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'bhub-tile-board-editor',
  templateUrl: './tile-board-editor.component.html',
  styleUrls: ['./tile-board-editor.component.scss'],
  standalone: false
})
export class TileBoardEditorComponent implements AfterViewInit, AfterViewChecked {
  @ViewChildren('tileTemplate', { read: ViewContainerRef }) tileTemplates!: QueryList<ViewContainerRef>;

  @Input() board: UserBoard;
  @Input() mode: 'create' | 'edit' = 'create';
  @Output() saveBoard = new EventEmitter<UserBoard>();
  @Output() cancelEdit = new EventEmitter<void>();

  gridsterOptions: GridsterConfig;
  filterBars: FilterBar[] = [];
  selectedFilterBar: FilterBar = null;
  renderTilesNextLoop: boolean = false;

  constructor(
    private filterBarService: FilterBarService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private cfr: ComponentFactoryResolver
  ) {}

  ngOnInit() {
    // Initialize board if creating new
    if (!this.board) {
      this.board = this.createNewBoard();
    }

    // Setup gridster options
    this.gridsterOptions = {
      gridType: GridType.Fixed,
      compactType: CompactType.None,
      margin: 10,
      outerMargin: true,
      useTransformPositioning: true,
      mobileBreakpoint: 640,
      minCols: 1,
      maxCols: 24,
      minRows: 1,
      maxRows: 18,
      fixedColWidth: 75,
      fixedRowHeight: 75,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: 20,
      enableEmptyCellClick: true,
      enableEmptyCellContextMenu: true,
      enableEmptyCellDrop: true,
      enableEmptyCellDrag: true,
      draggable: {
        enabled: true,
      },
      resizable: {
        enabled: true,
      },
      pushItems: true,
      pushResizeItems: true,
      displayGrid: DisplayGrid.Always
    };

    // Load filter bars
    this.filterBarService.getFilterBars().subscribe((response) => {
      var newLocalReferenceFilterBars: FilterBar[] = [];
      response.forEach((element) => {
        newLocalReferenceFilterBars.push(cloneDeep(element));
      });
      this.filterBars = newLocalReferenceFilterBars;

      // Set selected filter bar if editing
      if (this.board.filterBarId) {
        this.selectedFilterBar = this.filterBars.find(f => f.filterBarId === this.board.filterBarId);
      }
    });

    // Convert component types if editing
    if (this.mode === 'edit' && this.board.itemList) {
      this.convertItemComponents();
    }
  }

  ngAfterViewInit(): void {
    this.renderTileComponents();
  }

  ngAfterViewChecked(): void {
    if (this.renderTilesNextLoop) {
      this.renderTileComponents();
      this.renderTilesNextLoop = false;
    }
  }

  createNewBoard(): UserBoard {
    const now = new Date().toISOString().split('T')[0];
    const newBoard = new UserBoard();
    newBoard.userBoardName = 'New Tile Board';
    newBoard.itemList = [];
    newBoard.userBoardId = '';
    newBoard.filterBarId = '';
    newBoard.userBoardDescription = '';
    newBoard.boardType = UserBoardTypes.TileBoard;
    newBoard.isShared = false;
    newBoard.isDefault = false;
    newBoard.sharedTo = [];
    newBoard.createdDate = now;
    newBoard.modifiedDate = now;
    newBoard.createdBy = '';
    newBoard.modifiedBy = '';
    return newBoard;
  }

  convertItemComponents(): void {
    this.board.itemList.forEach((item: UserBoardItem) => {
      switch (item.componentType) {
        case 'heroTile':
          item.data.component = HeroTileComponent;
          item.data.mode = UserBoardComponentMode.Preview;
          break;
        case 'chartTile':
          item.data.component = ChartTileComponent;
          item.data.mode = UserBoardComponentMode.Preview;
          break;
        case 'tableTile':
          item.data.component = TableTileComponent;
          item.data.mode = UserBoardComponentMode.Preview;
          break;
        case 'pieChartTile':
          item.data.component = PieTileComponent;
          item.data.mode = UserBoardComponentMode.Preview;
          break;
        default:
          item.data.component = HeroTileComponent;
          item.data.mode = UserBoardComponentMode.Preview;
      }
    });
  }

  renderTileComponents(): void {
    if (this.board && this.board.itemList && this.tileTemplates) {
      this.tileTemplates.toArray().forEach((template, index) => {
        if (this.board.itemList[index]) {
          const tileItem = this.board.itemList[index];
          const factory = this.cfr.resolveComponentFactory(tileItem.data.component);
          const componentRef: ComponentRef<any> = template.createComponent(factory);

          componentRef.instance.data = tileItem.data.data;
          componentRef.instance.mode = tileItem.data.mode;

          // Handle output events
          if (componentRef.instance.onPreviewItemClickEvent) {
            componentRef.instance.onPreviewItemClickEvent.subscribe((icon: string) => {
              this.onPreviewIconClicked(icon, tileItem.id);
            });
          }
        }
      });
    }
  }

  onPreviewIconClicked(icon: string, tileId: string): void {
    switch (icon) {
      case 'edit':
        this.editComponent(tileId);
        break;
      case 'delete':
        const index = this.board.itemList.findIndex(item => item.id === tileId);
        if (index !== -1) {
          this.board.itemList.splice(index, 1);
        }
        break;
      default:
        break;
    }
  }

  onFilterBarChange(selectedFilterBarId: string): void {
    const selectedFilterBar = this.filterBars.find(f => f.filterBarId === selectedFilterBarId);
    if (selectedFilterBar) {
      this.selectedFilterBar = selectedFilterBar;
      this.board.filterBarId = selectedFilterBarId;
    } else {
      this.selectedFilterBar = null;
    }
  }

  addComponent(): void {
    const dialogRef = this.dialog.open(BoardItemCreatorComponent, {
      width: '95%',
      height: '90%',
      maxHeight: '90vh',
      data: new BoardItemCreatorModalObject(false, true),
      panelClass: 'center-dialog'
    });

    dialogRef.afterClosed().subscribe((userBoardItem: UserBoardItem) => {
      if (userBoardItem) {
        this.addTileToGrid(userBoardItem);
      }
    });
  }

  editComponent(itemId: string): void {
    const boardItem = this.board.itemList.find(item => item.id === itemId);

    const dialogRef = this.dialog.open(BoardItemCreatorComponent, {
      width: '95%',
      height: '90%',
      maxHeight: '90vh',
      data: new BoardItemCreatorModalObject(false, true, boardItem),
      panelClass: 'center-dialog'
    });

    dialogRef.afterClosed().subscribe((userBoardItem: UserBoardItem) => {
      if (userBoardItem) {
        const index = this.board.itemList.findIndex(item => item.id === userBoardItem.id);
        if (index !== -1) {
          this.board.itemList[index] = userBoardItem;
          this.renderTileComponents();
        }
      }
    });
  }

  addTileToGrid(userBoardItem: UserBoardItem): void {
    const emptyCell = this.findEmptyCell();
    userBoardItem.x = emptyCell.x;
    userBoardItem.y = emptyCell.y;
    userBoardItem.cols = 1;
    userBoardItem.rows = 1;

    this.board.itemList.push(userBoardItem);
    this.cdr.detectChanges();
    this.renderTileComponents();
  }

  findEmptyCell(): { x: number, y: number } {
    for (let y = 0; y < this.gridsterOptions.maxRows; y++) {
      for (let x = 0; x < this.gridsterOptions.maxCols; x++) {
        const isOccupied = this.board.itemList.some(item => item.x === x && item.y === y);
        if (!isOccupied) {
          return { x, y };
        }
      }
    }
    return { x: 0, y: 0 };
  }

  save(): void {
    this.saveBoard.emit(this.board);
  }

  cancel(): void {
    this.cancelEdit.emit();
  }
}
